/**
 * Convert UTC ISO string to local time string for display
 * @param {string} utcISO - UTC ISO string (e.g., "2026-01-16T09:00:00.000Z")
 * @param {string} format - Format: '12hour' | '24hour'
 * @returns {string} Local time string
 */
export const utcISOToLocalTime = (utcISO: string, format: '12hour' | '24hour' = '12hour'): string => {
  if (!utcISO) return '';
  
  const date = new Date(utcISO);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const mins = minutes.toString().padStart(2, '0');
  
  if (format === '24hour') {
    return `${hours.toString().padStart(2, '0')}:${mins}`;
  }
  
  // 12-hour format
  let hour12 = hours % 12;
  if (hour12 === 0) hour12 = 12;
  const period = hours < 12 ? 'AM' : 'PM';
  return `${hour12}:${mins} ${period}`;
};

/**
 * Parse slot string (format: "startISO-endISO") and convert to local time display
 * @param {string} slotString - Slot string in format "2026-01-16T09:00:00.000Z-2026-01-16T10:00:00.000Z"
 * @returns {string} Formatted time slot (e.g., "9:00 AM - 10:00 AM")
 */
export const parseSlotToLocalTime = (slotString: string): string => {
  if (!slotString) return '';
  
  // Handle slot string format: "startISO-endISO"
  // The split might have more than 2 parts if the ISO string contains dashes
  // So we need to split on the first occurrence of "-" that separates the two ISO strings
  // ISO format: YYYY-MM-DDTHH:mm:ss.sssZ
  // We'll split on "-" and then reconstruct: first 3 parts are date, then T, then time, then Z
  // Actually, better approach: split on the pattern that separates two ISO strings
  // The separator is "-" between "Z" and the next date
  
  const parts = slotString.split('-');
  if (parts.length < 2) return slotString;
  
  // Find where the first ISO string ends (look for "Z" followed by another date pattern)
  // ISO format: YYYY-MM-DDTHH:mm:ss.sssZ
  // So we need to find the "Z" and split there
  const zIndex = slotString.indexOf('Z');
  if (zIndex === -1) return slotString;
  
  // Split at Z + 1 (after the Z)
  const startISO = slotString.substring(0, zIndex + 1);
  const endISO = slotString.substring(zIndex + 2); // Skip the "-" after Z
  
  if (!startISO || !endISO) {
    console.warn('Invalid slot format:', slotString);
    return slotString;
  }
  
  try {
    const startLocal = utcISOToLocalTime(startISO, '12hour');
    const endLocal = utcISOToLocalTime(endISO, '12hour');
    
    return `${startLocal} - ${endLocal}`;
  } catch (error) {
    console.error('Error parsing slot time:', error, { slotString, startISO, endISO });
    return slotString;
  }
};

/**
 * Extract start and end ISO strings from slot string
 * @param {string} slotString - Slot string in format "startISO-endISO"
 * @returns {Object} Object with start and end ISO strings
 */
export const parseSlotISO = (slotString: string): { start: string; end: string } => {
  if (!slotString) return { start: '', end: '' };
  
  // Find the first 'Z' which marks the end of the first ISO string
  const firstZIndex = slotString.indexOf('Z');
  if (firstZIndex === -1) {
    console.warn('Invalid slotString format: Z not found', slotString);
    return { start: '', end: '' };
  }

  const start = slotString.substring(0, firstZIndex + 1);
  // The second ISO string starts immediately after the '-' following the first 'Z'
  const end = slotString.substring(firstZIndex + 2); // +2 to skip 'Z' and '-'
  
  return { start: start || '', end: end || '' };
};
